import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useFieldError, useT } from '@kmgestao/i18n';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  toast,
} from '@kmgestao/ui';

import {
  INVITABLE_ROLE_KEYS,
  inviteClinicMemberSchema,
  useInviteClinicMember,
  useRolesCatalog,
  type InviteClinicMemberInput,
} from '../../../features/clinic-team/index.js';

export interface InviteMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clinicId: string;
}

/**
 * Dialog modal pra convidar novo membro pra equipe da clinic.
 * Disparado pelo botão "Convidar membro" na `ClinicTeamPage`.
 *
 * **3 campos**: nome completo, email, papel. Submit chama edge
 * function `invite-clinic-member` (cria user + member + magic link
 * + email). Em sucesso, fecha dialog e reseta form.
 *
 * Roles vem do catálogo `roles` (filtrado por whitelist
 * `INVITABLE_ROLE_KEYS` — owner/superadmin/patient excluídos).
 */
export function InviteMemberDialog({ open, onOpenChange, clinicId }: InviteMemberDialogProps) {
  const { t } = useT('clinics');
  const { t: tCommon } = useT('common');
  const tError = useFieldError();
  const rolesQ = useRolesCatalog();
  const mutation = useInviteClinicMember();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InviteClinicMemberInput>({
    resolver: zodResolver(inviteClinicMemberSchema),
    defaultValues: {
      email: '',
      full_name: '',
      role_key: 'medico',
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      const result = await mutation.mutateAsync({ clinic_id: clinicId, ...values });
      if (result.action === 'already_invited') {
        toast.info(t('team.invite.feedback.alreadyInvited'));
      } else if (result.email_status === 'sent') {
        toast.success(t('team.invite.feedback.successTitle'), {
          description: t('team.invite.feedback.successDescription', { email: values.email }),
        });
      } else {
        toast.warning(t('team.invite.feedback.emailFailedTitle'), {
          description: t('team.invite.feedback.emailFailedDescription'),
        });
      }
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(t('team.invite.feedback.errorTitle'), {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  });

  // Filtra roles do catálogo pelo whitelist (somente convidáveis).
  const invitableRoles =
    rolesQ.data?.filter((r) => (INVITABLE_ROLE_KEYS as readonly string[]).includes(r.key)) ?? [];

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('team.invite.title')}</DialogTitle>
          <DialogDescription>{t('team.invite.description')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label htmlFor="invite-full-name">{t('team.invite.fields.fullName.label')}</Label>
            <Input
              id="invite-full-name"
              type="text"
              autoComplete="name"
              placeholder={t('team.invite.fields.fullName.placeholder')}
              hasError={!!errors.full_name}
              {...register('full_name')}
            />
            <p className="text-sm text-muted-foreground">
              {tError(errors.full_name) ?? t('team.invite.fields.fullName.hint')}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="invite-email">{t('team.invite.fields.email.label')}</Label>
            <Input
              id="invite-email"
              type="email"
              autoComplete="email"
              inputMode="email"
              placeholder={t('team.invite.fields.email.placeholder')}
              hasError={!!errors.email}
              {...register('email')}
            />
            <p className="text-sm text-muted-foreground">
              {tError(errors.email) ?? t('team.invite.fields.email.hint')}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="invite-role">{t('team.invite.fields.roleKey.label')}</Label>
            <Select
              id="invite-role"
              hasError={!!errors.role_key}
              disabled={rolesQ.isLoading}
              {...register('role_key')}
            >
              {invitableRoles.map((role) => (
                <option key={role.key} value={role.key}>
                  {role.name_pt_br}
                </option>
              ))}
            </Select>
            <p className="text-sm text-muted-foreground">
              {tError(errors.role_key) ?? t('team.invite.fields.roleKey.hint')}
            </p>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                {tCommon('actions.cancel')}
              </Button>
            </DialogClose>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? tCommon('status.saving') : t('team.invite.actions.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
